import { TextField } from '@mui/material';
import {
  getGridStringOperators,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
} from '@mui/x-data-grid-premium';
import {
  ChangeEvent, CompositionEvent, useEffect, useRef, useState,
} from 'react';

const DEBOUNCE_MS = 500;

// IME 変換中は検索(applyValue)を確定させないフィルタ入力。
// - 変換中(compositionstart〜compositionend)は onChange で値を保持するだけで applyValue しない。
// - 変換確定(compositionend)後、および非 IME 入力は debounce して applyValue する（既定同様に打鍵ごとの過剰検索を避ける）。
export const GridFilterInputImeValue = (props: GridFilterInputValueProps) => {
  const { item, applyValue, focusElementRef } = props;
  const [value, setValue] = useState<string>((item.value as string) ?? '');
  const composingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setValue((item.value as string) ?? '');
  }, [item.value]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const commit = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => applyValue({ ...item, value: next }), DEBOUNCE_MS);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setValue(next);
    // IME 変換中は確定させない（compositionend で適用）
    if (!composingRef.current) commit(next);
  };

  const handleCompositionEnd = (event: CompositionEvent<HTMLInputElement>) => {
    composingRef.current = false;
    commit((event.target as HTMLInputElement).value);
  };

  return (
    <TextField
      inputRef={focusElementRef}
      value={value}
      onChange={handleChange}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={handleCompositionEnd}
      label="値"
      placeholder="値を入力…"
      variant="outlined"
      size="small"
    />
  );
};

// 単一テキスト入力のオペレータ（contains 等）だけ IME 対応入力に差し替える。
// isEmpty/isNotEmpty(入力なし)・isAnyOf(複数入力) は既定のまま。
const SINGLE_TEXT_OPERATORS = ['contains', 'doesNotContain', 'equals', 'doesNotEqual', 'startsWith', 'endsWith'];

export const IME_STRING_FILTER_OPERATORS: GridFilterOperator[] = getGridStringOperators()
  .map((operator) => (SINGLE_TEXT_OPERATORS.includes(operator.value)
    ? { ...operator, InputComponent: GridFilterInputImeValue }
    : operator));

// テキスト列（type が number でなく、フィルタ可能で、独自 filterOperators を持たない列）の
// フィルタ入力を IME 対応に一括で差し替える。
export const withImeStringFilter = (columns: GridColDef[]): GridColDef[] => columns.map((column) => (
  column.type !== 'number' && column.filterable !== false && !column.filterOperators
    ? { ...column, filterOperators: IME_STRING_FILTER_OPERATORS }
    : column
));

export type { GridFilterItem };
